import { Tooltip, Typography } from "@mui/material";
import { InPlaceEditorProps, Labeled, useRecordContext } from "react-admin";
import { AegleInPlaceEditor } from "./AegleInPlaceEditor";
import { ContractBasisInput } from "@/components/common/inputs/ContractBasisInput";
import { displayContractBasis } from "@/utils/contractBasis";

const getByPath = (record: Record<string, unknown> | undefined, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>((acc, key) => (acc && typeof acc === "object" ? (acc as never)[key] : undefined), record);

const ContractBasisDisplay = ({ source }: { source: string }) => {
  const record = useRecordContext();
  const value = getByPath(record as Record<string, unknown> | undefined, source) as string | null | undefined;
  return (
    <Typography variant="body1" sx={{ display: "block", mb: 0.5 }}>
      {displayContractBasis(value)}
    </Typography>
  );
};

/**
 * In-place editor for a contract basis field (#7320). Shows "N / M"; editing
 * opens the two-number {@link ContractBasisInput} so the stored value is always
 * the canonical "N/M" the API expects.
 */
const InPlaceContractBasisEditor = ({
  source,
  label,
  readOnly = false,
  readOnlyReason,
  ...rest
}: {
  source: string;
  label?: string;
  readOnly?: boolean;
  readOnlyReason?: string;
} & Omit<InPlaceEditorProps, "input" | "source">) => {
  if (readOnly) {
    return (
      <Tooltip title={readOnlyReason}>
        <span>
          <Labeled label={label}>
            <ContractBasisDisplay source={source} />
          </Labeled>
        </span>
      </Tooltip>
    );
  }

  return (
    <Labeled label={label}>
      <AegleInPlaceEditor source={source} editor={<ContractBasisInput source={source} compact autoFocus />} {...rest}>
        <ContractBasisDisplay source={source} />
      </AegleInPlaceEditor>
    </Labeled>
  );
};

export default InPlaceContractBasisEditor;
